import { Component, forwardRef, ElementRef, ViewChild, Input, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, Validator, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { IgxDatePickerComponent } from 'igniteui-angular';
import moment from 'moment';

@Component({
    selector: 'input-date',
    templateUrl: './input-date.component.html',
    styleUrls: ['./input-date.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputDateComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputDateComponent),
            multi: true,
        }
    ],
    standalone: false
})
export class InputDateComponent implements ControlValueAccessor, Validator, OnInit {

  @ViewChild('inputPicker', { static: true }) inputPicker: ElementRef<HTMLInputElement>;
  @ViewChild('datePicker') datePicker: IgxDatePickerComponent;

  @Input() disableMinutes = false;

  innerFormGroup: UntypedFormGroup;

  hours = Array(24).fill(0).map((_, i) => (('00' + i).slice(-2)));
  minutes = Array(60).fill(0).map((_, i) => ('00' + i).slice(-2));

  private onTouchedCallback = () => { };
  private onChangeCallback = (_: Date) => { };

  // カレンダーが開かれているか
  private isCalendarOpened = false;

  get value() {
    if (this.innerFormGroup.invalid) { return null; }
    const d = this.innerFormGroup.get('date').value,
      h = this.innerFormGroup.get('hour').value,
      m = this.innerFormGroup.get('minute').value;
    const res = this.joinDateTime(d, h, m);
    return res ? res.toDate() : null;
  }

  set value(value: Date) {
    const obj = { date: null, hour: null, minute: null };
    if (value) {
      const dm = moment(value);
      obj.date = value;
      obj.hour = ('00' + dm.get('h')).slice(-2);
      obj.minute = ('00' + dm.get('m')).slice(-2);
    }
    this.innerFormGroup.setValue(obj);
  }

  get date() { return this.innerFormGroup.get('date'); }
  get hour() { return this.innerFormGroup.get('hour'); }
  get minute() { return this.innerFormGroup.get('minute'); }

  constructor(
    formBuilder: UntypedFormBuilder,
  ) {
    this.innerFormGroup = formBuilder.group({
      date: [null, this.validateDate.bind(this)],
      hour: [null],
      minute: [null]
    });

    this.date.valueChanges.subscribe(v => {
      if (!v) { return; }
      const obj = {
        hour: this.hour.value || '00',
        minute: this.minute.value || '00'
      };
      this.innerFormGroup.patchValue(obj);
    });

    this.innerFormGroup.valueChanges.subscribe(_ => {
      this.onChangeCallback(this.value);
    });
  }

  ngOnInit() {
    // 分が非活性化されている場合の処理
    if (this.disableMinutes) {
      // 分を00のみに設定
      this.minutes = ['00'];

      // 分のコントロールを無効化し、00に設定
      setTimeout(() => {
        this.minute.setValue('00');
        this.minute.disable();
      });
    }
  }

  registerOnChange(fn: any) { this.onChangeCallback = fn; }

  registerOnTouched(fn: any) { this.onTouchedCallback = fn; }

  writeValue(value: Date) {
    this.value = value;
    if (this.datePicker) {
      this.formatInputElementValue(this.datePicker.element.nativeElement as HTMLInputElement);
    }
  }

  validate() {
    if (!this.date.value) {
      return null;
    }
    const year = moment(this.date.value).format('YYYY');
    if (year.length !== 4) {
      return { validateDateError: true, validateDatePickerError: true };
    }
    if (!this.hour.value) {
      return { validateHourError: true, validateDatePickerError: true };
    }
    if (!this.minute.value) {
      return { validateMinuteError: true, validateDatePickerError: true };
    }
    return null;
  }

  validateDate() {
    if (!this.inputPicker) { return null; }
    const value = this.inputPicker.nativeElement.value;
    if (value && value.length > 0) {
      const y = new Date(value).getFullYear();
      return (isNaN(y) || y > 9999) ? { yearInputError: true } : null;
    }
    return null;
  }

  onDatePickerOpened() {
    this.isCalendarOpened = true;
  }

  onDatePickerClosed() {
    this.formatInputElementValue(this.datePicker.element.nativeElement as HTMLInputElement);
    this.isCalendarOpened = false;
  }

  private formatInputElementValue(element: HTMLInputElement) {
    if (this.date.invalid || !this.date.value) {
      this.innerFormGroup.setValue({ hour: null, minute: null, date: this.date.value });
      return;
    }
    // レンダリングのタイミングで切り替わらない時があるのでanimationFrameで実行
    requestAnimationFrame(() => element.value = moment(this.date.value).format('YYYY/MM/DD'));
  }

  private joinDateTime(date: Date, hour: string, minute: string) {
    if (!date || !hour || !minute) { return null; }
    const d = moment(date);
    d.set('h', Number(hour));
    d.set('m', Number(minute));
    d.startOf('m');
    return d;
  }

  // todayボタンイベント
  public moveToday() {
    this.datePicker.selectToday();
  }

  // カレンダーから日付を選択した時のイベント
  public onSelection() {
    setTimeout(() => {
      if (!this.isCalendarOpened && !this.date.value) {
        this.innerFormGroup.setValue({ hour: null, minute: null, date: this.date.value });
      }
    });
    // 日付入力欄からフォーカスを外す
    (document.activeElement as HTMLElement).blur();
  }
}
